/**
 * Orders Dashboard - For Hotel Admin/Manager
 * Shows all service orders and food orders with status tracking
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Search, RefreshCw, Clock, CheckCircle2, AlertTriangle, 
  Coffee, UtensilsCrossed, Timer, Filter, Eye
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Status badge component
const StatusBadge = ({ status, servedWithinTime }) => {
  const statusConfig = {
    pending: { label: 'Pending', class: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    accepted: { label: 'Accepted', class: 'bg-blue-100 text-blue-800 border-blue-200' },
    in_progress: { label: 'In Progress', class: 'bg-blue-100 text-blue-800 border-blue-200' },
    served: { 
      label: servedWithinTime === false ? 'Served (Delayed)' : 'Served', 
      class: servedWithinTime === false ? 'bg-orange-100 text-orange-800 border-orange-200' : 'bg-green-100 text-green-800 border-green-200' 
    },
    completed: { label: 'Completed', class: 'bg-green-100 text-green-800 border-green-200' },
    cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-800 border-red-200' },
    preparing: { label: 'Preparing', class: 'bg-blue-100 text-blue-800 border-blue-200' },
    ready: { label: 'Ready', class: 'bg-purple-100 text-purple-800 border-purple-200' },
    delivered: { label: 'Delivered', class: 'bg-green-100 text-green-800 border-green-200' },
  };
  
  const config = statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
  
  return (
    <Badge variant="outline" className={config.class}>
      {config.label}
    </Badge>
  );
};

export default function OrdersDashboard({ user }) {
  const [activeTab, setActiveTab] = useState('services');
  const [serviceOrders, setServiceOrders] = useState([]);
  const [foodOrders, setFoodOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [servicesRes, foodRes] = await Promise.all([
        api.get('/service-orders').catch(() => ({ data: [] })),
        // Use /orders endpoint with order_type filter for room service orders
        api.get('/orders?order_type=room_service').catch(() => ({ data: [] }))
      ]);
      setServiceOrders(servicesRes.data || []);
      setFoodOrders(foodRes.data || []);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Filter orders
  const filterOrders = (orders) => {
    return orders.filter(order => {
      // Status filter
      if (statusFilter !== 'all' && order.status !== statusFilter) return false;
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const roomMatch = (order.room_number || '').toLowerCase().includes(query);
        const guestMatch = (order.guest_name || '').toLowerCase().includes(query);
        const serviceMatch = (order.service_name || order.item_name || '').toLowerCase().includes(query);
        if (!roomMatch && !guestMatch && !serviceMatch) return false;
      }
      
      return true;
    });
  };

  const filteredServiceOrders = filterOrders(serviceOrders);
  const filteredFoodOrders = filterOrders(foodOrders);

  // Calculate stats
  const serviceStats = {
    total: serviceOrders.length,
    pending: serviceOrders.filter(o => o.status === 'pending').length,
    inProgress: serviceOrders.filter(o => ['accepted', 'in_progress'].includes(o.status)).length,
    completed: serviceOrders.filter(o => ['served', 'completed'].includes(o.status)).length,
    delayed: serviceOrders.filter(o => o.served_within_time === false).length,
  };

  const foodStats = {
    total: foodOrders.length,
    pending: foodOrders.filter(o => o.status === 'pending').length,
    preparing: foodOrders.filter(o => ['preparing', 'in_progress'].includes(o.status)).length,
    completed: foodOrders.filter(o => ['delivered', 'completed'].includes(o.status)).length,
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  return (
    <div className="p-6 space-y-6" data-testid="orders-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Orders Dashboard</h1>
          <p className="text-gray-500">Monitor all service and food orders</p>
        </div>
        <Button onClick={loadData} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{serviceStats.pending + foodStats.pending}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Timer className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{serviceStats.inProgress + foodStats.preparing}</p>
                <p className="text-xs text-gray-500">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{serviceStats.completed + foodStats.completed}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{serviceStats.delayed}</p>
                <p className="text-xs text-gray-500">Delayed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by room, guest, or service..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="served">Served</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="services" className="gap-2">
            <Coffee className="h-4 w-4" />
            Service Orders ({filteredServiceOrders.length})
          </TabsTrigger>
          <TabsTrigger value="food" className="gap-2">
            <UtensilsCrossed className="h-4 w-4" />
            Food Orders ({filteredFoodOrders.length})
          </TabsTrigger>
        </TabsList>

        {/* Service Orders Tab */}
        <TabsContent value="services">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Service Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredServiceOrders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Coffee className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p>No service orders found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Room</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Guest</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredServiceOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-medium">{order.room_number || 'N/A'}</TableCell>
                        <TableCell>
                          {order.service_name}
                          {order.quantity > 1 && <span className="text-xs text-gray-500 ml-1">(x{order.quantity})</span>}
                        </TableCell>
                        <TableCell>{order.guest_name || '-'}</TableCell>
                        <TableCell>₹{order.amount || 0}</TableCell>
                        <TableCell>
                          <StatusBadge status={order.status} servedWithinTime={order.served_within_time} />
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => openOrderDetails(order)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Food Orders Tab */}
        <TabsContent value="food">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Food Orders (Room Service)</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredFoodOrders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <UtensilsCrossed className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p>No food orders found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Room</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Guest</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFoodOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-medium">{order.room_number || 'N/A'}</TableCell>
                        <TableCell>
                          {order.items?.length || 0} items
                        </TableCell>
                        <TableCell>{order.guest_name || '-'}</TableCell>
                        <TableCell>₹{order.total_amount || order.amount || 0}</TableCell>
                        <TableCell>
                          <StatusBadge status={order.status} />
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => openOrderDetails(order)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Order Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Room</p>
                  <p className="font-medium">{selectedOrder.room_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <StatusBadge status={selectedOrder.status} servedWithinTime={selectedOrder.served_within_time} />
                </div>
                <div>
                  <p className="text-gray-500">Guest</p>
                  <p className="font-medium">{selectedOrder.guest_name || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Amount</p>
                  <p className="font-medium">₹{selectedOrder.amount || selectedOrder.total_amount || 0}</p>
                </div>
              </div>
              
              {selectedOrder.service_name && (
                <div>
                  <p className="text-gray-500 text-sm">Service</p>
                  <p className="font-medium">{selectedOrder.service_name}</p>
                  {selectedOrder.quantity > 1 && <p className="text-sm text-gray-500">Quantity: {selectedOrder.quantity}</p>}
                </div>
              )}
              
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Items</p>
                  <div className="space-y-1">
                    {selectedOrder.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>{item.name} x{item.quantity}</span>
                        <span>₹{item.price * item.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedOrder.notes && (
                <div>
                  <p className="text-gray-500 text-sm">Notes</p>
                  <p className="text-sm bg-gray-50 p-2 rounded">{selectedOrder.notes}</p>
                </div>
              )}
              
              <div className="border-t pt-4 text-xs text-gray-500 grid grid-cols-2 gap-2">
                <div>
                  <p>Created</p>
                  <p>{new Date(selectedOrder.created_at).toLocaleString('en-IN')}</p>
                </div>
                {selectedOrder.accepted_at && (
                  <div>
                    <p>Accepted</p>
                    <p>{new Date(selectedOrder.accepted_at).toLocaleString('en-IN')}</p>
                  </div>
                )}
                {selectedOrder.served_at && (
                  <div>
                    <p>Served</p>
                    <p>{new Date(selectedOrder.served_at).toLocaleString('en-IN')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
